import React from 'react';
import { Gift, ArrowRight, Clock } from 'lucide-react';

/**
 * Big, hard-to-miss homepage banner that surfaces the first featured promo
 * across all tours. Renders nothing if no tour has a promo_banner.
 *
 * Props:
 *   tours: Array of tour objects from /api/tours (each may have promo_banner field)
 *   onCTA: (tour) => void   — handler when "Claim now" is tapped
 */
const HomepagePromoStrip = ({ tours = [], onCTA }) => {
  // Find first tour with an active featured promo
  const tour = tours.find((t) => t && t.promo_banner && t.promo_banner.code);
  if (!tour) return null;

  const banner = tour.promo_banner;
  const isFree = banner.discount_type === 'free' || banner.discount_percent === 100;

  const tag =
    (banner.banner_tag && banner.banner_tag.trim()) ||
    (isFree ? 'FREE TOUR' : `${banner.discount_percent}% OFF`);

  const headline =
    (banner.headline && banner.headline.trim()) ||
    (isFree
      ? `${tour.title} is FREE this week`
      : `Save ${banner.discount_percent}% on ${tour.title}`);

  const subtext =
    (banner.banner_subtext && banner.banner_subtext.trim()) ||
    `Use code ${banner.code} at checkout — limited time`;

  const ctaLabel =
    (banner.banner_cta && banner.banner_cta.trim()) || 'Claim Free Tour';

  const expiryText = (() => {
    if (!banner.expires_at) return null;
    try {
      const d = new Date(banner.expires_at);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return null;
    }
  })();

  return (
    <section
      data-testid="homepage-promo-strip"
      className="relative overflow-hidden bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 text-white shadow-xl"
    >
      {/* Decorative blurs */}
      <div className="absolute -top-16 -right-16 w-72 h-72 bg-white/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-16 -left-16 w-72 h-72 bg-white/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4 py-6 sm:py-8 flex flex-col sm:flex-row items-center gap-5 sm:gap-8">
        {/* Icon */}
        <div className="bg-white/25 backdrop-blur rounded-full p-4 shrink-0">
          <Gift className="w-8 h-8 text-white" />
        </div>

        {/* Copy */}
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start flex-wrap gap-2 mb-2">
            <span className="inline-block bg-white text-orange-600 font-black uppercase tracking-widest text-xs px-3 py-1 rounded-full shadow-sm">
              {tag}
            </span>
            {expiryText && (
              <span className="inline-flex items-center gap-1 text-xs bg-white/20 backdrop-blur px-2 py-1 rounded-full">
                <Clock className="w-3 h-3" />
                Ends {expiryText}
              </span>
            )}
          </div>
          <h3
            className="text-2xl sm:text-3xl font-black leading-tight drop-shadow-sm"
            data-testid="homepage-promo-headline"
          >
            {headline}
          </h3>
          <p className="text-white/95 text-sm sm:text-base mt-1 font-semibold">{subtext}</p>
          <p className="text-white/90 text-xs sm:text-sm mt-2">
            Code:{' '}
            <code className="inline-block bg-white text-orange-600 font-mono font-black tracking-wider px-2 py-0.5 rounded ml-1">
              {banner.code}
            </code>
          </p>
        </div>

        {/* CTA */}
        <button
          type="button"
          onClick={() => onCTA && onCTA(tour)}
          data-testid="homepage-promo-cta"
          className="w-full sm:w-auto bg-white text-orange-600 hover:bg-orange-50 font-black uppercase tracking-wide text-sm px-6 py-4 rounded-full shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2"
        >
          {ctaLabel}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
};

export default HomepagePromoStrip;
